import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {getOrder, getOrderSettingsInfo} from "../../api/api";
import Loader from "../../components/Loader";
import "./ShortBill.css";

const DOCUMENT_TITLES = {
    TAX_INVOICE: 'Tax Invoice',
    BILL_OF_SUPPLY: 'Bill of Supply',
    INVOICE: 'Invoice',
};

// Width the print head typically reaches on each roll (80mm: 576 dots, 58mm: 384 dots at
// 203dpi). Generic POS drivers report the whole roll as printable and drop whatever falls
// past the head, so the bill must be no wider than this. short_bill_print_width_mm (read off
// the alignment test print) overrides it per printer.
//
// The @page is always the full paper width with the bill flush left inside it: a page
// narrower than the driver's paper gets centred by Chrome, which pushes the bill right and
// makes it disagree with the alignment ruler (also printed at full paper width).
export const PRINTABLE_WIDTH_MM = {'58': 48, '80': 72};

export const getPaperWidthMm = (settings) => (settings?.short_bill_paper_width === '58' ? 58 : 80);

export const getPrintWidthMm = (settings) => {
    const paperWidth = String(getPaperWidthMm(settings));
    return settings?.short_bill_print_width_mm || PRINTABLE_WIDTH_MM[paperWidth];
};

// Bootstrap's print CSS forces body{min-width:992px}, which makes Chrome shrink the
// receipt to fit the page. This class lets ShortBill.css undo that for this page only.
export const useShortBillBody = () => {
    useEffect(() => {
        document.body.classList.add('short-bill-body');
        return () => document.body.classList.remove('short-bill-body');
    }, []);
};

const money = (value) => Number(value || 0).toFixed(2);

function ShortBill() {
    const {id} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = location.state?.returnTo;
    const printedRef = useRef(false);
    const billRef = useRef(null);
    const [billHeightMm, setBillHeightMm] = useState(null);

    useShortBillBody();

    const orderQuery = useQuery({
        queryKey: ['order', id],
        queryFn: () => getOrder(id),
        retry: false,
    });
    const settingsQuery = useQuery({
        queryKey: ['order-settings'],
        queryFn: getOrderSettingsInfo,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });

    const order = orderQuery.data;
    const settings = settingsQuery.data?.results?.[0];
    const paperWidth = getPaperWidthMm(settings);
    const printableWidth = getPrintWidthMm(settings);
    const ready = !!order && !settingsQuery.isLoading;

    // @page needs a concrete height ("80mm auto" is invalid CSS and gets dropped), so size
    // the page to the rendered receipt: roll printers then feed only as much paper as needed.
    useLayoutEffect(() => {
        if (!ready || !billRef.current) return;
        const px = billRef.current.getBoundingClientRect().height;
        // Keep the page taller than wide, or it is treated as landscape and printed rotated
        setBillHeightMm(Math.max(Math.ceil(px * 25.4 / 96) + 1, paperWidth + 5));
    }, [ready, paperWidth, printableWidth]);

    useEffect(() => {
        if (!ready || !billHeightMm || printedRef.current) return;

        const handleAfterPrint = () => {
            if (returnTo) navigate(returnTo);
        };
        window.addEventListener('afterprint', handleAfterPrint);
        // Let the receipt paint before the print dialog snapshots it
        const timer = setTimeout(() => {
            printedRef.current = true;
            window.print();
        }, 300);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, [ready, billHeightMm, returnTo, navigate]);

    if (orderQuery.isError) {
        return (
            <div className="short-bill-page">
                <div className="short-bill-toolbar">
                    <span>Could not load this order.</span>
                    <button type="button" onClick={() => navigate('/sell/orders')}>Back to orders</button>
                </div>
            </div>
        );
    }

    if (!ready) {
        return <div className="short-bill-page"><Loader/></div>;
    }

    const created = new Date(order.created);
    const addressLine = [order.merchant_business_address1, order.merchant_business_city]
        .filter(Boolean).join(', ');

    return (
        <div className="short-bill-page">
            {billHeightMm && <style>{`@page { size: ${paperWidth}mm ${billHeightMm}mm; margin: 0; }`}</style>}

            <div className="short-bill-toolbar">
                <button type="button" onClick={() => window.print()}>Print again</button>
                <button type="button" onClick={() => navigate(returnTo || '/sell/cart')}>Back to cart</button>
                <button type="button" onClick={() => navigate('/sell/orders')}>Orders</button>
            </div>
            <div className="short-bill-tip">
                Tip: in the print dialog, open More settings and set Margins to <b>Minimum</b> (Chrome remembers it).
                Other options leave blank paper above the bill.
            </div>

            <div className="short-bill" ref={billRef} style={{width: `${printableWidth}mm`}}>
                <div className="sb-center">
                    <div className="sb-doc-title">{DOCUMENT_TITLES[order.document_type] || 'Bill'}</div>
                    <div className="sb-business">{order.merchant_business_name}</div>
                    {addressLine && <div className="sb-small">{addressLine}</div>}
                    {order.merchant_business_gstin && <div className="sb-small">GSTIN: {order.merchant_business_gstin}</div>}
                </div>

                {order.token_number != null && (
                    <div className="sb-token">
                        <div className="sb-token-label">TOKEN</div>
                        <div className="sb-token-number">{order.token_number}</div>
                    </div>
                )}

                <div className="sb-rule"/>
                <div className="sb-row sb-small">
                    <span>Order: {order.order_number}</span>
                    <span>{created.toLocaleDateString('en-IN')}</span>
                </div>
                <div className="sb-row sb-small">
                    <span>{order.invoice_number ? `Bill: ${order.invoice_number}` : ''}</span>
                    <span>{created.toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit'})}</span>
                </div>
                <div className="sb-rule"/>

                <div className="sb-row sb-head">
                    <span className="sb-item">Item</span>
                    <span className="sb-qty">Qty</span>
                    <span className="sb-amt">Amt</span>
                </div>
                {order.order_items.map((item) => (
                    <div key={item.id}>
                        <div className="sb-row">
                            <span className="sb-item">{item.name}</span>
                            <span className="sb-qty">{item.quantity}</span>
                            <span className="sb-amt">{money(item.total_amount)}</span>
                        </div>
                        {(item.add_on_products || []).map((addOn, index) => (
                            <div key={index} className="sb-addon">+ {addOn.name}</div>
                        ))}
                    </div>
                ))}
                <div className="sb-rule"/>

                <div className="sb-row"><span>Sub Total</span><span>{money(order.sub_total_before_discount)}</span></div>
                {Number(order.order_discount) > 0 && (
                    <div className="sb-row"><span>Discount</span><span>-{money(order.order_discount)}</span></div>
                )}
                {Number(order.cgst) > 0 && <div className="sb-row sb-small"><span>CGST</span><span>{money(order.cgst)}</span></div>}
                {Number(order.sgst) > 0 && <div className="sb-row sb-small"><span>SGST</span><span>{money(order.sgst)}</span></div>}
                {Number(order.utgst) > 0 && <div className="sb-row sb-small"><span>UTGST</span><span>{money(order.utgst)}</span></div>}
                {Number(order.igst) > 0 && <div className="sb-row sb-small"><span>IGST</span><span>{money(order.igst)}</span></div>}
                <div className="sb-row sb-total"><span>TOTAL</span><span>₹ {money(order.total_amount)}</span></div>
                <div className="sb-row sb-small">
                    <span>{order.payment_method || 'UNPAID'}</span>
                    <span>{order.payment_status}</span>
                </div>
                <div className="sb-rule"/>

                <div className="sb-center sb-small">
                    {settings?.short_bill_footer && <div>{settings.short_bill_footer}</div>}
                    <div className="sb-powered-by">Powered by blackx.io</div>
                </div>
            </div>
        </div>
    );
}

export default ShortBill;
